import React, { useEffect, useState } from "react";
import { DownloadSimple, X, DeviceMobile } from "@phosphor-icons/react";

const DISMISS_KEY = "tnai_pwa_install_dismissed_at";
const DISMISS_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

/**
 * Install-to-home-screen banner. Appears when:
 *   • Chrome / Edge / Android: the browser fires `beforeinstallprompt`.
 *   • iOS Safari: no event fires, so we show a hint explaining the manual
 *     "Share → Add to Home Screen" flow (iOS has no programmatic install).
 * Hides itself in standalone mode (already installed) and for 7 days after
 * dismissal.
 */
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Running in standalone / installed mode — never show.
    const installed = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    if (installed) return;

    // Dismissed recently — honour it.
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < DISMISS_WINDOW_MS) return;

    const ua = window.navigator.userAgent || "";
    const iosLike = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    if (iosLike && isSafari) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const installedHandler = () => setVisible(false);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch { /* ignore */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 sm:px-4 sm:pb-4 pointer-events-none"
         data-testid="pwa-install-banner">
      <div className="pointer-events-auto max-w-md mx-auto bg-slate-900 text-white rounded-sm shadow-2xl border border-slate-700 p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-blue-600 grid place-items-center shrink-0">
          <DeviceMobile size={18} weight="fill"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm">Install TNAI Sales Suite</div>
          {isIOS ? (
            <div className="text-[11px] text-slate-300 mt-0.5 leading-relaxed">
              Tap the <b className="text-white">Share</b> icon in Safari, then <b className="text-white">Add to Home Screen</b> for a native-app feel.
            </div>
          ) : (
            <div className="text-[11px] text-slate-300 mt-0.5">
              Add it to your home screen for a full-screen, app-like experience with offline shell.
            </div>
          )}
          {!isIOS && (
            <button onClick={install} data-testid="pwa-install-cta"
                    className="mt-2 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-sm px-3 py-1.5 transition-colors">
              <DownloadSimple size={13}/>Install app
            </button>
          )}
        </div>
        <button onClick={dismiss} data-testid="pwa-install-dismiss"
                className="text-slate-400 hover:text-white p-1 -m-1 shrink-0" aria-label="Dismiss">
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}
